import { pageurl } from '@constants/pageurl';
import { useRightPanelContext } from '@context/right-section';
import { type IActiveElectionData } from '@interface/IElection';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import agentimg from '../../../assets/images/agents.jpg';
import { type IActions } from '../../../interface/IAction';
import { type IStates } from '../../../interface/IReducer';
import DataWrapper from '../../../wrapper/data-wrapper';
import { ElectionFiltersContainer, parseElectionName } from './components/election-filters';
import {
	AvailableElectionCard,
	LandingHeader,
	LandingPageAvailableElections,
	LandingPageContent,
	LandingPageHero,
	LandingPageWrapper,
	NoResultsMessage,
} from './styled';
import { IELECT_MARK_DATA_URL } from '../../../assets/images/ielect-mark-data';

interface LPChild {
	states?: IStates;
}

export const pageSectionEnum = {
	LANDING: 'landing',
	AGENT: 'agent',
} as const;

export type pageSectionType = (typeof pageSectionEnum)[keyof typeof pageSectionEnum];

const CalendarIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 16 16'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M4.5 1a.5.5 0 0 0-.5.5V2H2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2v-.5a.5.5 0 0 0-1 0V2H5v-.5a.5.5 0 0 0-.5-.5zM2 6v8h12V6H2z' />
	</svg>
);

const statusConfig = {
	active: { label: 'Open for applications', color: 'var(--color-status-success)' },
	upcoming: { label: 'Opens soon', color: 'var(--color-status-warning)' },
	closed: { label: 'Closed', color: 'var(--color-text-tertiary)' },
};

// Community-Green tag families (color / tint) per election type
const typeStyles: Record<string, { color: string; bg: string }> = {
	'Governorship': { color: '#1e5631', bg: '#e5f0e8' },
	'Presidential': { color: '#92662a', bg: '#f8eed8' },
	'Senate': { color: '#4a548f', bg: '#e8e9f5' },
	'Senatorial': { color: '#4a548f', bg: '#e8e9f5' },
	'House of Rep': { color: '#8f4a4a', bg: '#f5e8e8' },
	'House of Reps': { color: '#8f4a4a', bg: '#f5e8e8' },
	'House of Assembly': { color: '#1e5631', bg: '#e5f0e8' },
	'Local Government': { color: '#92662a', bg: '#f8eed8' },
	'Councilorship': { color: '#4a548f', bg: '#e8e9f5' },
	'Election': { color: '#4a5a4f', bg: '#eee9dc' },
};

const LandingPageChild: React.FC<LPChild> = ({ states: reduxStates, ...props }) => {
	const { getPartyGuid, getElectionGuid, setPageSection, getAllActiveElections, getStates, getLGAsInState, getWardsInLGA } =
		props as unknown as IActions;
	const navigate = useNavigate();
	const rightPanel = useRightPanelContext();
	const electionsRef = useRef<HTMLDivElement>(null);
	const [filteredElections, setFilteredElections] = useState<IActiveElectionData[]>([]);

	useEffect(() => {
		setPageSection(pageSectionEnum.LANDING);
		getAllActiveElections();
	}, []);

	const location = useLocation();
	const { search } = location;
	const { party, election } = queryString.parse(search);

	useEffect(() => {
		getElectionGuid(election as string);
		getPartyGuid(party as string);
	}, []);

	const onApply = (item: IActiveElectionData) => () => {
		rightPanel.callSection({
			action: 'create',
			component: 'select-party',
			title: `Apply — ${item.name}`,
			data: {
				id: item.id,
				electionName: item.name,
			},
		});
	};

	const activeElections = reduxStates?.election?.getAllActiveElections?.data;
	const locationStates = reduxStates?.location?.getAllLocationStates?.data;
	const locationStatesLoading = reduxStates?.location?.getAllLocationStates_Loading ?? false;
	const locationLgas = reduxStates?.location?.getAllLocationLGAsInState?.data;
	const locationLgasLoading = reduxStates?.location?.getAllLocationLGAsInState_Loading ?? false;
	const locationWards = reduxStates?.location?.getAllLocationWardsInLGA?.data;
	const locationWardsLoading = reduxStates?.location?.getAllLocationWardsInLGA_Loading ?? false;

	const handleFetchStates = useCallback(() => {
		getStates();
	}, [getStates]);

	const handleFetchLgas = useCallback(
		(stateId: number) => {
			getLGAsInState({ stateIds: [stateId] });
		},
		[getLGAsInState]
	);

	const handleFetchWards = useCallback(
		(lgaId: number) => {
			getWardsInLGA({ lgaIds: [lgaId] });
		},
		[getWardsInLGA]
	);

	const handleFilteredElectionsChange = useCallback((elections: IActiveElectionData[]) => {
		setFilteredElections(elections);
	}, []);

	const scrollToElections = () => electionsRef.current?.scrollIntoView({ behavior: 'smooth' });

	return (
		<LandingPageWrapper>
			<LandingHeader>
				<button
					type='button'
					className='brand'
					onClick={() => navigate(pageurl.OVERVIEW)}
					aria-label='iVolunteer home'
				>
					<img
						className='brand-mark'
						src={IELECT_MARK_DATA_URL}
						alt=''
					/>
					<span className='brand-name'>iVolunteer</span>
				</button>
				<div className='nav'>
					<button
						type='button'
						className='nav-link'
						onClick={scrollToElections}
					>
						Elections
					</button>
					<button
						type='button'
						className='nav-link'
						onClick={() => navigate(pageurl.INSTRUCTIONS)}
					>
						How it works
					</button>
					<button
						type='button'
						className='apply-cta'
						onClick={scrollToElections}
					>
						Apply now
					</button>
				</div>
			</LandingHeader>

			<LandingPageContent>
				<LandingPageHero>
					<div className='hero-copy'>
						<span className='eyebrow'>iElect Volunteer Program</span>
						<h1>
							Your community. Your vote. <em>Your watch.</em>
						</h1>
						<p className='subcopy'>
							Become a paid election observer or party agent at your local polling unit — earn up to <strong>₦150,000</strong> per
							election day.
						</p>
						<div className='hero-cta'>
							<button
								type='button'
								className='btn-primary'
								onClick={scrollToElections}
							>
								Find your election
							</button>
							<button
								type='button'
								className='btn-ghost'
								onClick={() => navigate(pageurl.INSTRUCTIONS)}
							>
								How it works
							</button>
						</div>
					</div>

					<div className='hero-media'>
						<div className='media-tall'>
							<img
								src={agentimg}
								alt='Election agent at a polling unit'
							/>
						</div>
						<div className='media-col'>
							<div className='media-short' />
							<div className='media-stat'>
								<span className='stat-num'>12,400+</span>
								<span className='stat-label'>agents deployed</span>
							</div>
						</div>
					</div>
				</LandingPageHero>

				<LandingPageAvailableElections ref={electionsRef}>
					<div className='elections-head'>
						<h2>Elections near you</h2>
					</div>

					<ElectionFiltersContainer
						elections={activeElections}
						states={locationStates}
						statesLoading={locationStatesLoading}
						lgas={locationLgas}
						lgasLoading={locationLgasLoading}
						wards={locationWards}
						wardsLoading={locationWardsLoading}
						onFetchStates={handleFetchStates}
						onFetchLgas={handleFetchLgas}
						onFetchWards={handleFetchWards}
						onFilteredElectionsChange={handleFilteredElectionsChange}
					/>

					<div className='content'>
						{filteredElections.length > 0 ? (
							filteredElections.map((item, index) => {
								const parsed = parseElectionName(item.name);
								const statusInfo = statusConfig[parsed.status];
								const typeStyle = typeStyles[parsed.electionType] || typeStyles.Election;
								const isActive = parsed.status === 'active';

								return (
									<AvailableElectionCard
										key={index}
										onClick={isActive ? onApply(item) : undefined}
										role='button'
										tabIndex={isActive ? 0 : -1}
										aria-disabled={!isActive}
										onKeyDown={(e: React.KeyboardEvent) => {
											if (isActive && (e.key === 'Enter' || e.key === ' ')) {
												e.preventDefault();
												onApply(item)();
											}
										}}
										style={{ animationDelay: `${index * 60}ms` } as React.CSSProperties}
									>
										<div className='card-top'>
											<span
												className='type-badge'
												style={{ backgroundColor: typeStyle.bg, color: typeStyle.color }}
											>
												{parsed.electionType}
											</span>
											<span
												className='status-label'
												style={{ color: statusInfo.color }}
											>
												<span className='dot' />
												{statusInfo.label}
											</span>
										</div>

										<h3>{parsed.name}</h3>

										{(parsed.formattedDate || parsed.location) && (
											<div className='meta'>
												{parsed.formattedDate && (
													<span className='with-icon'>
														<CalendarIcon />
														{parsed.formattedDate}
													</span>
												)}
												{parsed.formattedDate && parsed.location && <span className='dot' />}
												{parsed.location && <span>{parsed.location}</span>}
											</div>
										)}

										<span className={isActive ? 'card-cta' : 'card-cta is-disabled'}>{isActive ? 'Apply' : 'Opens soon'}</span>
									</AvailableElectionCard>
								);
							})
						) : (
							<NoResultsMessage>
								{activeElections?.length
									? 'No elections match your filters. Try adjusting your search.'
									: 'No elections available'}
							</NoResultsMessage>
						)}
					</div>
				</LandingPageAvailableElections>
			</LandingPageContent>
		</LandingPageWrapper>
	);
};

const LPChildMemo = React.memo(LandingPageChild);

const LandingPage: React.FC = () => (
	<DataWrapper>
		<LPChildMemo />
	</DataWrapper>
);

export default LandingPage;
